import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import {
    FinanceIQCreateSession,
    FinanceIQCreateConversation,
    FinanceIQEntities,
    FinanceIQHealth,
    FinanceIQStreamUrl,
} from "../../services/process/chatbot/financeiqService";

// ── Reasoning traces per entity ───────────────────────────────────────────────
const TRACES = {
    portfolio:    [
        { dot: "#4a90d9", label: "Entity resolved",    text: "Portfolio schema matched — injecting nav, benchmark, risk_profile" },
        { dot: "#4aaa7a", label: "Context enriched",   text: "YTD filter applied, unrealized_pnl pulled from Positions" },
        { dot: "#c8a040", label: "Calculation",        text: "YTD_Return = (NAV_current − NAV_start) / NAV_start × 100" },
        { dot: "#7a70d9", label: "Compliance checked", text: "No mandate breaches for reporting window" },
    ],
    positions:    [
        { dot: "#4aaa7a", label: "Entity resolved", text: "Positions entity matched — loading active holdings" },
        { dot: "#4a90d9", label: "Sort applied",    text: "Ranked by market_value descending" },
        { dot: "#c8a040", label: "Weight check",    text: "weight_pct sum validated ✓" },
    ],
    risk:         [
        { dot: "#c8a040", label: "Risk schema",     text: "Risk Metrics matched — injecting var_95, sharpe, beta, vol_ann" },
        { dot: "#4a90d9", label: "Lookback window", text: "252-day rolling window applied" },
        { dot: "#d86040", label: "Threshold check", text: "VaR vs mandate limit checked" },
        { dot: "#4aaa7a", label: "Narrative built", text: "Basel III VaR framing, 95% confidence" },
    ],
    transactions: [
        { dot: "#d86040", label: "Transaction schema", text: "Transactions matched — date range filter applied" },
        { dot: "#4a90d9", label: "Direction parsed",   text: "BUY / SELL aggregated from ENUM field" },
        { dot: "#c8a040", label: "TCA computed",       text: "Slippage vs VWAP benchmark calculated" },
        { dot: "#7a70d9", label: "Audit stamped",      text: "audit_trail_id stamped for all trades" },
    ],
    compliance:   [
        { dot: "#7a70d9", label: "Compliance schema", text: "Checking mandate_breach, concentration, exclusions" },
        { dot: "#d85050", label: "Breach scan",       text: "All positions scanned against concentration_limit" },
        { dot: "#c8a040", label: "ESG filter",        text: "Excluded sectors cross-referenced" },
        { dot: "#4aaa7a", label: "Audit generated",   text: "Session audit_trail_id stamped" },
    ],
};

const CONTEXT_BADGES = {
    portfolio:    ["entity:Portfolio", "field:nav", "field:unrealized_pnl", "calc:YTD_Return"],
    positions:    ["entity:Positions", "field:ticker", "field:market_value", "field:weight_pct"],
    risk:         ["entity:Risk", "field:var_95", "field:sharpe_ratio", "field:beta"],
    transactions: ["entity:Transactions", "field:direction", "field:execution_price", "calc:Slippage_bps"],
    compliance:   ["entity:Compliance", "field:mandate_breach", "field:concentration_limit"],
};

const SUGGESTIONS = {
    portfolio:    ["Show top 5 positions", "Sector attribution", "Compare to benchmark"],
    positions:    ["Top 10 by weight", "Underperformers", "Asset class breakdown"],
    risk:         ["Stress test scenarios", "Factor exposures", "Concentration breakdown"],
    transactions: ["Full TCA breakdown", "Settlement report", "Pre-trade analytics"],
    compliance:   ["ESG score breakdown", "Rebalance recommendations", "Regulatory filings"],
};

const DEFAULT_SUGGESTIONS = ["Show portfolio performance", "Run risk report", "Check compliance status", "Recent trade activity"];

const DEFAULT_TRACE = [
    { dot: "#4aaa7a", label: "Schema loaded",   text: "5 metadata entities indexed" },
    { dot: "#4a90d9", label: "Session context", text: "Mistral via Ollama — zero rate limits" },
    { dot: "#7a70d9", label: "Ready",           text: "Awaiting your first query" },
];

const generateId = () => {
    if (crypto.randomUUID) return crypto.randomUUID();
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
};

export function useFinanceIQChat() {
    // Read user from sessionStorage exactly like your PoCreation component
    const [userCredentials] = useState(
        JSON.parse(sessionStorage.getItem("userCredentials"))
    );

    const [messages,       setMessages]       = useState([]);
    const [streamingText,  setStreamingText]  = useState("");
    const [isStreaming,    setIsStreaming]     = useState(false);
    const [suggestions,    setSuggestions]    = useState(DEFAULT_SUGGESTIONS);
    const [reasoningTrace, setReasoningTrace] = useState(DEFAULT_TRACE);
    const [activeEntity,   setActiveEntity]   = useState("Portfolio");
    const [tokenCount,     setTokenCount]     = useState(0);
    const [activeScopes,   setActiveScopes]   = useState(["Equities", "Q4-2024"]);
    const [backendStatus,  setBackendStatus]  = useState({ ok: null, message: "Checking..." });
    const [entities,       setEntities]       = useState([]);
    const [sessionId,      setSessionId]      = useState(null);
    const [convId,         setConvId]         = useState(null);
    const [error,          setError]          = useState(null);

    const messagesRef = useRef([]);
    const initialized = useRef(false);

    // Get token from sessionStorage (same pattern your project uses for auth)
    const getAuthToken = () => sessionStorage.getItem("financeiq_token") ||
                                sessionStorage.getItem("authToken") ||
                                sessionStorage.getItem("token") || "";

    const addMessage = useCallback((role, text, contexts = [], tags = []) => {
        const msg = {
            id:        generateId(),
            role, text, contexts, tags,
            timestamp: new Date().toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" }),
        };
        messagesRef.current = [...messagesRef.current, msg];
        setMessages([...messagesRef.current]);
    }, []);

    // Init: health check → create session → create conversation → load entities
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const init = async () => {
            try {
                // 1. Health check — axios, your pattern
                const healthCreds = FinanceIQHealth();
                const healthResp  = await axios.get(healthCreds[1], healthCreds[0]);
                setBackendStatus({ ok: healthResp.data.ok, message: healthResp.data.message });

                // 2. Create session
                const sessCreds  = FinanceIQCreateSession();
                const sessResp  = await axios.post(
                    sessCreds[1],
                    { 
                        active_scopes: ["Equities", "Q4-2024"],
                        user_id:       userCredentials?.user_id,
                        user_name:     userCredentials?.user_name,
                        entity_id:     userCredentials?.selectedentity,
                        tenant_id:     userCredentials?.tenants_id,
                    },
                    sessCreds[0]
                );
                setSessionId(sessResp.data.session_id);

                // 3. Create conversation
                const convCreds  = FinanceIQCreateConversation();
                const convResp   = await axios.post(
                    convCreds[1],
                    { session_id: sessResp.data.session_id, title: "Main",user_id: userCredentials?.user_id },
                    convCreds[0]
                );
                setConvId(convResp.data.conversation_id);

                // 4. Load metadata schema for left panel
                const entCreds   = FinanceIQEntities();
                const entResp    = await axios.get(entCreds[1], entCreds[0]);
                setEntities(entResp.data);

                // 5. Welcome message
                addMessage("ai",
                    `Connected to FinanceIQ.\n\nOllama is running Mistral — no rate limits, no external API.\n\nEntity: ${userCredentials?.selectedentity || "N/A"} | Role: ${userCredentials?.groups_id || "ANALYST"}\n\nWhat would you like to explore?`,
                    ["entity:Portfolio", "entity:Risk", "entity:Compliance"],
                    ["mistral", "schema-connected"]
                );

            } catch (err) {
                console.error("FinanceIQ init error:", err);
                setBackendStatus({ ok: false, message: err.message });
                setError(err.message);
                addMessage("ai",
                    `Cannot reach Django backend: ${err.message}\n\nMake sure Django is running on port 8000.`,
                    [], ["error"]
                );
            }
        };

        init();
    }, []); // eslint-disable-line

    // SSE streaming — uses fetch because axios doesn't stream
    const sendMessage = useCallback(async (userText) => {
        if (!userText.trim() || isStreaming || !convId) return;

        addMessage("user", userText, [], []);
        setIsStreaming(true);
        setStreamingText("");

        const streamUrl = FinanceIQStreamUrl();
        const token     = getAuthToken();

        let fullText = "";

        try {
            const response = await fetch(streamUrl, {
                method:  "POST",
                headers: {
                    "Content-Type":  "application/json",
                    ...(token ? { "Authorization": `Token ${token}` } : {}),
                },
                body: JSON.stringify({
                    conversation_id: convId,
                    message:         userText,user: userCredentials,
                    active_scopes:   activeScopes,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader  = response.body.getReader();
            const decoder = new TextDecoder();
            let   buffer  = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop();

                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    const raw = line.slice(6).trim();
                    if (!raw) continue;

                    try {
                        const frame = JSON.parse(raw);

                        if (frame.type === "meta") {
                            setActiveEntity(frame.entity || "Portfolio");
                            setReasoningTrace(frame.trace || TRACES[frame.entity] || DEFAULT_TRACE);
                            setSuggestions(frame.suggestions || SUGGESTIONS[frame.entity] || DEFAULT_SUGGESTIONS);
                        } else if (frame.type === "token") {
                            fullText += frame.content;
                            setStreamingText(prev => prev + frame.content);
                        } else if (frame.type === "done") {
                            setStreamingText("");
                            addMessage("ai", fullText,
                                CONTEXT_BADGES[frame.entity] || [],
                                ["mistral", "metadata-grounded"]
                            );
                            setTokenCount(prev => prev + Math.floor(fullText.length / 4));
                            break;
                        } else if (frame.type === "error") {
                            throw new Error(frame.message);
                        }
                    } catch { /* skip malformed */ }
                }
            }

        } catch (err) {
            console.error("FinanceIQ stream error:", err);
            setStreamingText("");
            addMessage("ai", `Error: ${err.message}`, [], ["error"]);
            setBackendStatus({ ok: false, message: err.message });
            setError(err.message);
        } finally {
            setIsStreaming(false);
        }
    }, [isStreaming, convId, activeScopes, addMessage]);

    const toggleScope = useCallback((label) => {
        setActiveScopes(prev =>
            prev.includes(label) ? prev.filter(s => s !== label) : [...prev, label]
        );
    }, []);

    return {
        userCredentials,
        messages, streamingText, isStreaming,
        suggestions, reasoningTrace,
        activeEntity, tokenCount,
        activeScopes, backendStatus,
        entities, error,
        sendMessage, toggleScope,
    };
}
